import  { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axiosConfig from '../config/axiosConfig';

const WabaContext = createContext();

export const useWaba = () => {
  const context = useContext(WabaContext);
  if (!context) {
    // Return null values for components outside the provider (e.g., public PricingPage)
    return {
      wabaDetails: null,
      loadingWabaDetails: false,
      hasWabaDetails: false,
      wabaFetchError: false, // No error when outside provider
      refreshWabaDetails: () => Promise.resolve(),
      getWabaStatusDisplay: () => ({ text: 'Not Connected', color: '#ff4d4f', bgColor: '#fff2f0', borderColor: '#ffccc7' })
    };
  }
  return context;
};

export const WabaProvider = ({ children }) => {
  const [wabaDetails, setWabaDetails] = useState(null);
  const [loadingWabaDetails, setLoadingWabaDetails] = useState(true);
  const [wabaFetchError, setWabaFetchError] = useState(false); // Track if API had error (500, 429, etc.)

  // Derived state - true if we have valid WABA details
  // Only false when API succeeded but returned no data (user hasn't done signup)
  const hasWabaDetails = !!(wabaDetails?.waba_id && wabaDetails?.phone_number_id) || 
                         !!(wabaDetails && Object.keys(wabaDetails).length > 0);

  const fetchWabaDetails = useCallback(async () => {
    try {
      setLoadingWabaDetails(true);
      setWabaFetchError(false); // Reset error before fetching
      const response = await axiosConfig.get('/embeddedsignup/customer-waba-details');
      
      if (response.data.success && response.data.data) {
        // Handle both object and array responses
        const wabaData = Array.isArray(response.data.data) 
          ? response.data.data[0] 
          : response.data.data;
        setWabaDetails(wabaData);
        console.log('WABA Details fetched:', wabaData);
      } else {
        console.error('No WABA details found:', response.data.message);
        setWabaDetails(null);
        // API succeeded but no data - user hasn't done signup
        setWabaFetchError(false);
      }
    } catch (error) {
      console.error('Error fetching WABA details:', error);
      const statusCode = error?.response?.status;
      // For 500, 429, or network errors - set error flag, don't assume "not connected"
      if (statusCode === 500 || statusCode === 429 || !error?.response) {
        setWabaFetchError(true);
        // Keep previous wabaDetails state if we had data before
        // Don't set to null - we don't know the actual state
      } else {
        // For other errors (e.g., 401, 404), assume no WABA data
        setWabaDetails(null);
        setWabaFetchError(false);
      }
    } finally {
      setLoadingWabaDetails(false);
    }
  }, []);

  // Refresh function that can be called from child components
  const refreshWabaDetails = useCallback(async () => {
    await fetchWabaDetails();
  }, [fetchWabaDetails]);

  // Helper function to get status display
  const getWabaStatusDisplay = useCallback((status, details = wabaDetails) => {
    // If there was an API error, show unknown/checking status
    if (wabaFetchError) {
      return { text: 'Checking...', color: '#8c8c8c', bgColor: '#fafafa', borderColor: '#d9d9d9' };
    }
    // If we have valid WABA details, consider it connected regardless of status
    if (details?.waba_id && details?.phone_number_id) {
      switch (status?.toLowerCase()) {
        case 'active':
          return { text: 'Active', color: '#52c41a', bgColor: '#f6ffed', borderColor: '#b7eb8f' };
        case 'pending':
          return { text: 'Pending Activation', color: '#fa8c16', bgColor: '#fff7e6', borderColor: '#ffd591' };
        case 'inactive':
          return { text: 'Inactive', color: '#faad14', bgColor: '#fffbe6', borderColor: '#ffe58f' };
        case 'suspended':
          return { text: 'Suspended', color: '#ff4d4f', bgColor: '#fff2f0', borderColor: '#ffccc7' };
        default:
          return { text: 'Connected', color: '#52c41a', bgColor: '#f6ffed', borderColor: '#b7eb8f' };
      }
    } else {
      return { text: 'Not Connected', color: '#ff4d4f', bgColor: '#fff2f0', borderColor: '#ffccc7' };
    }
  }, [wabaDetails, wabaFetchError]);

  // Fetch WABA details on mount
  useEffect(() => {
    fetchWabaDetails();
  }, [fetchWabaDetails]);

  const value = {
    wabaDetails,
    loadingWabaDetails,
    hasWabaDetails,
    wabaFetchError, // Export error state for components to check
    refreshWabaDetails,
    getWabaStatusDisplay
  };

  return (
    <WabaContext.Provider value={value}>
      {children}
    </WabaContext.Provider>
  );
};

export default WabaContext;
